<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    persistent
    scrollable
    @update:model-value="onClose"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center modal-header-primary modal-header-primary-text">
        <span class="text-h6 modal-header-primary-text">Choisir l'historique</span>
        <v-btn
          icon
          variant="text"
          class="modal-header-primary-icon"
          aria-label="Fermer"
          @click="onClose(false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <p class="text-body-2 text-medium-emphasis mb-4">
          Par défaut : sessions prévues et en cours.
          Une année ajoute les sessions de cette année (y compris clôturées)
          <strong>en plus</strong> des sessions prévues / en cours actuelles.
        </p>

        <!-- Modes rapides -->
        <v-list lines="two" class="pa-0 mb-4">
          <v-list-item
            rounded="lg"
            class="mb-2 option-item"
            :active="currentMode === 'off'"
            color="primary"
            @click="chooseOff"
          >
            <template #prepend>
              <v-icon :color="currentMode === 'off' ? 'primary' : undefined">mdi-calendar-blank</v-icon>
            </template>
            <v-list-item-title>Sessions actives</v-list-item-title>
            <v-list-item-subtitle>Uniquement prévu et en cours</v-list-item-subtitle>
          </v-list-item>

          <v-list-item
            rounded="lg"
            class="mb-2 option-item"
            :active="currentMode === 'all'"
            color="primary"
            @click="chooseAll"
          >
            <template #prepend>
              <v-icon :color="currentMode === 'all' ? 'primary' : undefined">mdi-history</v-icon>
            </template>
            <v-list-item-title>Tout l'historique</v-list-item-title>
            <v-list-item-subtitle>Toutes les années confondues</v-list-item-subtitle>
          </v-list-item>
        </v-list>

        <!-- Saisie libre (chemin principal) -->
        <div class="text-subtitle-2 font-weight-bold mb-2">Année scolaire</div>
        <div class="d-flex flex-wrap ga-2 align-start mb-2">
          <v-text-field
            :model-value="manualAnnee"
            label="Ex. 2024/2025"
            placeholder="YYYY/YYYY"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="manualError ? [manualError] : []"
            class="flex-grow-1"
            style="min-width: 180px"
            maxlength="9"
            @update:model-value="onManualAnneeInput"
            @keyup.enter="applyManualAnnee"
          />
          <v-btn
            class="mydit-btn"
            :disabled="!manualAnnee.trim()"
            @click="applyManualAnnee"
          >
            Appliquer
          </v-btn>
        </div>
        <p class="text-caption text-medium-emphasis mb-4">
          Saisissez l'année souhaitée. Les suggestions ci-dessous sont optionnelles.
        </p>

        <v-progress-linear v-if="loading" indeterminate class="mb-3" color="primary" />
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
          {{ error }}
        </v-alert>

        <!-- Suggestions -->
        <div v-if="suggestionAnnees.length" class="mb-1">
          <div class="text-caption font-weight-medium text-medium-emphasis mb-2">
            Suggestions
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="a in suggestionAnnees"
              :key="a.annee_universitaire"
              :color="isAnneeSelected(a.annee_universitaire) ? 'primary' : undefined"
              :variant="isAnneeSelected(a.annee_universitaire) ? 'flat' : 'tonal'"
              size="small"
              class="suggestion-chip"
              @click="chooseAnnee(a.annee_universitaire)"
            >
              {{ a.label || a.annee_universitaire }}
              <span v-if="a.est_courante" class="ms-1 text-caption opacity-80">· en cours</span>
            </v-chip>
          </div>
        </div>

        <p
          v-else-if="!loading && !error"
          class="text-medium-emphasis text-body-2 mt-1"
        >
          Aucune suggestion disponible — saisissez une année ci-dessus, ou utilisez « Tout l'historique ».
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useHistoriqueMode } from '@/composables/useHistoriqueMode'
import { useSessionAnnees } from '@/composables/useSessionAnnees'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'changed'): void
}>()

const {
  mode: currentMode,
  anneeUniversitaire,
  setOff,
  setAll,
  selectAnnee,
} = useHistoriqueMode()

const { anneesData, loading, error, fetchAnnees } = useSessionAnnees()

const manualAnnee = ref('')
const manualError = ref('')
let lastManualValue = ''

/** Suggestions courtes : années courantes + archives récentes (max ~12) */
const suggestionAnnees = computed(() => {
  const all = anneesData.value?.annees || []
  const courantes = all.filter((a) => a.est_courante)
  const archives = all
    .filter((a) => !a.est_courante && a.archivable)
    .slice(0, 10)
  const seen = new Set<string>()
  const out: typeof all = []
  for (const a of [...courantes, ...archives]) {
    if (seen.has(a.annee_universitaire)) continue
    seen.add(a.annee_universitaire)
    out.push(a)
  }
  return out
})

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return
    manualError.value = ''
    manualAnnee.value =
      currentMode.value === 'annee' && anneeUniversitaire.value
        ? anneeUniversitaire.value
        : ''
    lastManualValue = manualAnnee.value
    await fetchAnnees()
  }
)

function onManualAnneeInput(val: string | null) {
  manualError.value = ''
  let next = String(val ?? '')
  const isDeleting = next.length < lastManualValue.length

  // Après 4 chiffres, insérer automatiquement le /
  if (!isDeleting && /^\d{4}$/.test(next)) {
    next = `${next}/`
  }

  // Garder un format raisonnable (chiffres + un slash)
  next = next.replace(/[^\d/]/g, '')
  const parts = next.split('/')
  if (parts.length > 2) {
    next = `${parts[0]}/${parts.slice(1).join('')}`
  }
  if (next.length > 9) next = next.slice(0, 9)

  manualAnnee.value = next
  lastManualValue = next
}

function normalizeAnnee(raw: string): string | null {
  const cleaned = raw.trim().replace(/\s+/g, '')
  // Accepte 2024/2025, 2024-2025, 2024
  const full = cleaned.match(/^(\d{4})[\/\-](\d{4})$/)
  if (full) {
    const y1 = Number(full[1])
    const y2 = Number(full[2])
    if (y2 !== y1 + 1) return null
    return `${y1}/${y2}`
  }
  const single = cleaned.match(/^(\d{4})$/)
  if (single) {
    const y1 = Number(single[1])
    return `${y1}/${y1 + 1}`
  }
  return null
}

function isAnneeSelected(annee: string) {
  return currentMode.value === 'annee' && anneeUniversitaire.value === annee
}

function onClose(v: boolean) {
  emit('update:modelValue', v)
}

function chooseOff() {
  setOff()
  emit('changed')
  emit('update:modelValue', false)
}

function chooseAll() {
  setAll()
  emit('changed')
  emit('update:modelValue', false)
}

function chooseAnnee(annee: string) {
  selectAnnee(annee)
  emit('changed')
  emit('update:modelValue', false)
}

function applyManualAnnee() {
  const normalized = normalizeAnnee(manualAnnee.value)
  if (!normalized) {
    manualError.value = 'Format attendu : YYYY/YYYY (ex. 2024/2025)'
    return
  }
  manualError.value = ''
  chooseAnnee(normalized)
}
</script>

<style scoped>
.option-item {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.modal-header-primary {
  background-color: #417985 !important;
  color: #fff !important;
  border-radius: 3px 3px 0 0;
}
.modal-header-primary-text {
  color: #fff !important;
}
.modal-header-primary-icon .v-icon {
  color: #fff !important;
}
.suggestion-chip {
  cursor: pointer;
}
</style>
